import {
  DEV_IMPERSONATION_TOKEN,
  DEV_ROLES_HEADER,
  DEV_USER_HEADER,
  DEV_USERS,
} from '@/features/auth/dev-impersonation';
import { useAuthStore } from '@/features/auth/auth-store';

/**
 * Shared API fetch client (SCRUM-448 / SCRUM-102 Phase C).
 *
 * A thin wrapper over `fetch` that attaches the App ID access token as
 * `Authorization: Bearer <token>` on every `/api/*` call when the auth store holds one,
 * and transparently recovers from an expired token: on a `401` whose body signals
 * `token_expired`, it runs the refresh hook once and retries the request a single time.
 *
 * Design notes:
 *  - It reads the token via `useAuthStore.getState()` (Zustand's non-React accessor), so it
 *    works from the plain `*-api.ts` modules without a hook.
 *  - When there is NO token (the demo-era path, or before the live App ID exchange is
 *    configured under SCRUM-431), no Authorization header is added — the request goes out
 *    exactly as before and the server treats it as unauthenticated. This keeps the currently
 *    deployed SPA working unchanged.
 *  - It does NOT touch the response body, status handling, or `Content-Type`: callers keep
 *    their own `res.ok` guards and zod parsing, and multipart/FormData requests are untouched
 *    (no forced Content-Type). It only augments headers and adds the 401-retry.
 *  - The token is never persisted (it lives in the memory-only auth store).
 */

/** The `error` code the backend returns in a 401 body when the access token has expired. */
const TOKEN_EXPIRED = 'token_expired';

/**
 * Window event dispatched when an `/api/*` call is rejected with `403 Forbidden` under real auth
 * (SCRUM-103, D7) — i.e. the authenticated caller's role does not grant the requested module.
 *
 * `apiFetch` is a plain module (no React tree), so it cannot call `useToast` directly. Instead it
 * dispatches this decoupled event; a single listener in the app shell surfaces an access-denied
 * toast. This keeps a forbidden response from rendering as a silent empty view (the failure mode
 * D7 calls out) while leaving the transport UI-agnostic. Consumers that already own a denied panel
 * (e.g. the Ask BankIQ page) keep it as defense-in-depth.
 */
export const ACCESS_DENIED_EVENT = 'bankiq:access-denied';

/** Detail payload for {@link ACCESS_DENIED_EVENT}: the request URL that was forbidden. */
export interface AccessDeniedEventDetail {
  url: string;
}

/** Reads the current App ID access token from the auth store (memory-only). */
function currentToken(): string | null {
  return useAuthStore.getState().accessToken;
}

/**
 * Attempt to obtain a fresh access token after a `token_expired` 401. Returns the new token,
 * or null if refresh isn't possible (in which case the caller surfaces the 401).
 *
 * INTERIM (SCRUM-448): there is no silent App ID refresh wired yet — refresh requires the live
 * Custom Identity exchange (SCRUM-431) and a stored refresh grant, neither of which exists in
 * the current inert state. Until then, an expired token cannot be refreshed in place, so this
 * clears the session (returns the user to sign-in) and returns null. The one-shot retry seam is
 * in place so wiring a real refresh later is a single-function change here — no call-site edits.
 */
async function refreshAccessToken(): Promise<string | null> {
  // Placeholder for the future silent re-exchange (SCRUM-431). For now, an expired token means
  // the session is over: clear it so the app returns to the login screen rather than looping.
  await Promise.resolve();
  useAuthStore.getState().signOut();
  return null;
}

/** True when a 401 response's JSON body carries the token_expired signal. */
async function isTokenExpired(response: Response): Promise<boolean> {
  if (response.status !== 401) {
    return false;
  }
  try {
    // Clone so the caller can still read the body if they choose to.
    const body = (await response.clone().json()) as { error?: string } | null;
    return body?.error === TOKEN_EXPIRED;
  } catch {
    return false;
  }
}

/** Resolve the request URL string from any `fetch` input form (string, URL, or Request). */
function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url; // Request
}

/** Merge the Authorization header into the caller's init without dropping their headers. */
function withBearer(init: RequestInit | undefined, token: string): RequestInit {
  const headers = new Headers(init?.headers);

  // ADR-014: a dev session carries the impersonation headers instead of a Bearer token.
  // `import.meta.env.DEV` is the literal `false` in a production build, so this whole
  // branch is dead code there and the header names never reach the bundle.
  if (import.meta.env.DEV && token === DEV_IMPERSONATION_TOKEN) {
    // The store holds the display role ("Teller"); the Host requires the stored
    // SystemRole name ("BranchEmployee"), which DEV_USERS carries. A session whose
    // email is not a DEV_USERS entry sends no impersonation headers, so the Host
    // treats the request as anonymous rather than as a guessed role.
    const user = useAuthStore.getState().user;
    const devUser = user ? DEV_USERS.find((u) => u.email === user.email) : undefined;
    if (devUser) {
      headers.set(DEV_USER_HEADER, devUser.email);
      headers.set(DEV_ROLES_HEADER, devUser.role);
    } else {
      console.error('apiFetch: dev impersonation session has no matching DEV_USERS entry.');
    }
    return { ...init, headers };
  }

  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

/**
 * `fetch` for BankIQ `/api/*` calls. Drop-in replacement for `fetch(input, init)`:
 * attaches the Bearer token when present and retries once on an expired-token 401.
 */
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const token = currentToken();
  const firstInit = token ? withBearer(init, token) : init;

  const response = await fetch(input, firstInit);

  // Only the token-carrying path can hit an expired-token 401 worth retrying.
  if (token && (await isTokenExpired(response))) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return fetch(input, withBearer(init, refreshed));
    }
  }

  // 403 under real auth: the role lacks the module grant (SCRUM-103). Signal the app shell to
  // surface an access-denied toast rather than letting the caller render a silent empty view.
  // Only meaningful on the token-carrying path — a token-less demo build never enforces, so it
  // never receives a policy 403. Guarded for non-browser (test/SSR) contexts.
  if (token && response.status === 403 && typeof window !== 'undefined') {
    const detail: AccessDeniedEventDetail = { url: requestUrl(input) };
    window.dispatchEvent(new CustomEvent(ACCESS_DENIED_EVENT, { detail }));
  }

  return response;
}
